import { useRef, useState } from 'react';
import { useCustom, useApiUrl } from '@refinedev/core';
import { useParams, useNavigate } from 'react-router-dom';
import { getIdToken } from '@/providers/auth-provider';
import { toast } from '@/hooks/useToast';
import { formatDate } from '@/lib/utils';
import { ReferralStatusBadge } from '@/components/referrals/ReferralStatusBadge';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import type { ReferralCampaign } from './list';
import { ChevronLeft, Plus, Copy, Link2, Users, X, Check, Star, Target, QrCode, Trash2, Ban, Play } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import {
  MEDIUM_TO_CHANNEL,
  SOURCE_PREFIXES,
  UTM_SOURCES,
  mediumsForSource,
  normalizeTag,
  validateUtmTuple,
} from '@/lib/utm-taxonomy';

interface ReferralLink {
  id: string;
  code: string;
  label: string | null;
  campaign_id: string | null;
  utm_source: string | null;
  status: string;
  click_count: number;
  install_count: number;
  signup_count: number;
  reward_count: number;
  ios_store_url: string | null;
  android_store_url: string | null;
  created_at: string;
}

interface ReferralReward {
  id: string;
  beneficiary_role: 'referrer' | 'referee';
  reward_type: 'coupon' | 'cash';
  amount_cents: number;
  currency: string;
  status: string;
  created_at: string;
}

type TabId = 'overview' | 'links' | 'rewards';

export function ReferralCampaignShowPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const apiUrl = useApiUrl();
  const { hasPermission } = useAdminPermissions();
  const canWrite = hasPermission('growth.referrals.write');
  const canPayout = hasPermission('growth.referrals.payout');

  const [tab, setTab] = useState<TabId>('overview');
  const [mintOpen, setMintOpen] = useState(false);
  const [qrLink, setQrLink] = useState<ReferralLink | null>(null);

  const { data: campaignData, isLoading, isError, refetch: refetchCampaign } = useCustom<ReferralCampaign>({
    url: `/v1/admin/referrals/campaigns/${id}`,
    method: 'get',
  });
  const campaign = campaignData?.data;

  const { data: linksData, refetch: refetchLinks } = useCustom<{ rows: ReferralLink[] }>({
    url: '/v1/admin/referrals/links',
    method: 'get',
    config: { query: { campaign_id: id, limit: '200' } },
  });
  const links = linksData?.data?.rows ?? [];

  const { data: rewardsData, refetch: refetchRewards } = useCustom<{ rows: ReferralReward[] }>({
    url: '/v1/admin/referrals/rewards',
    method: 'get',
    config: { query: { campaign_id: id, limit: '200' } },
  });
  const rewards = rewardsData?.data?.rows ?? [];

  const { data: scoreboardData } = useCustom<{
    totals: { clicks: number; signups: number; qualified: number; connects: number };
    links: Array<{ id: string; signups: number; qualified: number; connects: number }>;
  }>({
    url: `/v1/admin/referrals/campaigns/${id}/scoreboard`,
    method: 'get',
  });
  const scoreboard = scoreboardData?.data;
  const funnelByLink = new Map((scoreboard?.links ?? []).map((l) => [l.id, l]));

  const linkBase = apiUrl || 'https://api.myaimatchmaker.ai';

  const copyLink = (code: string) => {
    navigator.clipboard?.writeText(`${linkBase}/r/${code}`);
    toast({ title: 'Link copied', description: `${linkBase}/r/${code}` });
  };

  const transitionCampaign = async (status: 'active' | 'paused' | 'ended') => {
    try {
      const idToken = await getIdToken();
      const r = await fetch(`${apiUrl}/v1/admin/referrals/campaigns/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ status }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast({ title: `Campaign ${status}` });
      refetchCampaign();
    } catch (err: unknown) {
      toast({ title: 'Update failed', description: err instanceof Error ? err.message : 'error', variant: 'destructive' });
    }
  };

  const deleteCampaign = async () => {
    if (!window.confirm('Delete this campaign? Only possible when it has no tracking history.')) return;
    try {
      const idToken = await getIdToken();
      const r = await fetch(`${apiUrl}/v1/admin/referrals/campaigns/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({ message: 'Failed' }));
        throw new Error((e as { message?: string }).message ?? `HTTP ${r.status}`);
      }
      toast({ title: 'Campaign deleted' });
      navigate('/referrals');
    } catch (err: unknown) {
      toast({ title: 'Cannot delete', description: err instanceof Error ? err.message : 'error', variant: 'destructive' });
    }
  };

  const patchLink = async (linkId: string, body: Record<string, unknown>, okMsg: string) => {
    try {
      const idToken = await getIdToken();
      const r = await fetch(`${apiUrl}/v1/admin/referrals/links/${linkId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({ message: 'Failed' }));
        throw new Error((e as { message?: string }).message ?? `HTTP ${r.status}`);
      }
      toast({ title: okMsg });
      refetchLinks();
    } catch (err: unknown) {
      toast({ title: 'Update failed', description: err instanceof Error ? err.message : 'error', variant: 'destructive' });
    }
  };

  const deleteLink = async (linkId: string, code: string) => {
    if (!window.confirm(`Delete link "${code}"? Only possible when it has no tracking history.`)) return;
    try {
      const idToken = await getIdToken();
      const r = await fetch(`${apiUrl}/v1/admin/referrals/links/${linkId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({ message: 'Failed' }));
        throw new Error((e as { message?: string }).message ?? `HTTP ${r.status}`);
      }
      toast({ title: 'Link deleted' });
      refetchLinks();
    } catch (err: unknown) {
      toast({ title: 'Cannot delete', description: err instanceof Error ? err.message : 'error', variant: 'destructive' });
    }
  };

  const transitionReward = async (rewardId: string, action: string) => {
    try {
      const idToken = await getIdToken();
      const r = await fetch(`${apiUrl}/v1/admin/referrals/rewards/${rewardId}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({}),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({ message: 'Failed' }));
        throw new Error((e as { message?: string }).message ?? `HTTP ${r.status}`);
      }
      toast({ title: `Reward ${action.replace('_', ' ')}` });
      refetchRewards();
    } catch (err: unknown) {
      toast({ title: 'Action failed', description: err instanceof Error ? err.message : 'error', variant: 'destructive' });
    }
  };

  if (isLoading) return <div className="p-6 text-muted-foreground">Loading…</div>;
  if (isError || !campaign) return <div className="p-6 text-destructive">Failed to load campaign.</div>;

  const totalClicks = links.reduce((s, l) => s + (l.click_count || 0), 0);

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <button
        onClick={() => navigate('/referrals')}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to referrals
      </button>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            {campaign.name}
            <ReferralStatusBadge status={campaign.status} />
          </h1>
          <code className="text-sm text-muted-foreground">{campaign.slug}</code>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            {campaign.status !== 'active' && (
              <button onClick={() => transitionCampaign('active')} className="px-3 py-1.5 text-sm border rounded-md hover:bg-muted/30">Activate</button>
            )}
            {campaign.status === 'active' && (
              <button onClick={() => transitionCampaign('paused')} className="px-3 py-1.5 text-sm border rounded-md hover:bg-muted/30">Pause</button>
            )}
            {campaign.status !== 'ended' && (
              <button onClick={() => transitionCampaign('ended')} className="px-3 py-1.5 text-sm border rounded-md hover:bg-muted/30 text-destructive">End</button>
            )}
            <button onClick={deleteCampaign} title="Delete campaign (only without tracking history)" className="px-3 py-1.5 text-sm border rounded-md hover:bg-muted/30 text-destructive inline-flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </button>
          </div>
        )}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiCard icon={Users} label="Clicks" value={scoreboard?.totals.clicks ?? totalClicks} />
        <KpiCard icon={Link2} label="Signups" value={scoreboard?.totals.signups ?? 0} />
        <KpiCard icon={Target} label="Qualified" value={scoreboard?.totals.qualified ?? 0} />
        <KpiCard icon={Star} label="Connects ★ (north star)" value={scoreboard?.totals.connects ?? 0} />
      </div>

      {/* Tabs */}
      <div className="border-b mb-4 flex gap-1">
        {(['overview', 'links', 'rewards'] as TabId[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
              tab === t ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t === 'overview' ? 'Overview' : t === 'links' ? `Links (${links.length})` : `Rewards (${rewards.length})`}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="border rounded-lg p-6 bg-card grid gap-3 text-sm max-w-2xl">
          <Row label="Reward model" value={campaign.reward_model} />
          <Row label="Reward trigger" value={campaign.reward_trigger} />
          {campaign.reward_model === 'cash' && (
            <Row label="Referrer reward" value={`$${(campaign.referrer_reward_cents / 100).toFixed(2)}`} />
          )}
          <Row label="Environment" value={campaign.environment} />
          <Row label="Description" value={campaign.description || '—'} />
          <Row label="Created" value={formatDate(campaign.created_at)} />
        </div>
      )}

      {tab === 'links' && (
        <div>
          {canWrite && (
            <div className="flex justify-end mb-3">
              <button
                onClick={() => setMintOpen(true)}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90"
              >
                <Plus className="w-4 h-4" />
                Mint link
              </button>
            </div>
          )}
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr className="text-left">
                  <th className="px-4 py-3 font-medium">Code / Link</th>
                  <th className="px-4 py-3 font-medium">Label</th>
                  <th className="px-4 py-3 font-medium text-right">Clicks</th>
                  <th className="px-4 py-3 font-medium text-right">Signups</th>
                  <th className="px-4 py-3 font-medium text-right">Qualified</th>
                  <th className="px-4 py-3 font-medium text-right">Connects ★</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium w-12"></th>
                </tr>
              </thead>
              <tbody>
                {links.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No links yet.</td></tr>
                )}
                {links.map((l) => (
                  <tr key={l.id} className="border-b">
                    <td className="px-4 py-3">
                      <code className="font-medium">{l.code}</code>
                      <div className="text-xs text-muted-foreground">{linkBase}/r/{l.code}</div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{l.label || '—'}</td>
                    <td className="px-4 py-3 text-right">{l.click_count}</td>
                    <td className="px-4 py-3 text-right">{funnelByLink.get(l.id)?.signups ?? l.signup_count}</td>
                    <td className="px-4 py-3 text-right text-muted-foreground">{funnelByLink.get(l.id)?.qualified ?? 0}</td>
                    <td className="px-4 py-3 text-right font-medium">{funnelByLink.get(l.id)?.connects ?? 0}</td>
                    <td className="px-4 py-3"><ReferralStatusBadge status={l.status} size="sm" /></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => copyLink(l.code)} title="Copy link" className="text-muted-foreground hover:text-foreground mr-2">
                        <Copy className="w-4 h-4" />
                      </button>
                      <button onClick={() => setQrLink(l)} title="QR code" className="text-muted-foreground hover:text-foreground">
                        <QrCode className="w-4 h-4" />
                      </button>
                      {canWrite && (l.status === 'active' ? (
                        <button onClick={() => patchLink(l.id, { status: 'disabled' }, 'Link disabled')} title="Disable link (kill-switch — keeps history)" className="text-muted-foreground hover:text-foreground ml-2">
                          <Ban className="w-4 h-4" />
                        </button>
                      ) : (
                        <button onClick={() => patchLink(l.id, { status: 'active' }, 'Link re-enabled')} title="Re-enable link" className="text-muted-foreground hover:text-foreground ml-2">
                          <Play className="w-4 h-4" />
                        </button>
                      ))}
                      {canWrite && (
                        <button onClick={() => deleteLink(l.id, l.code)} title="Delete link (only without tracking history)" className="text-destructive/80 hover:text-destructive ml-2">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'rewards' && (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr className="text-left">
                <th className="px-4 py-3 font-medium">Beneficiary</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium text-right">Amount</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Created</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rewards.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No rewards yet.</td></tr>
              )}
              {rewards.map((rw) => (
                <tr key={rw.id} className="border-b">
                  <td className="px-4 py-3 capitalize">{rw.beneficiary_role}</td>
                  <td className="px-4 py-3 capitalize text-muted-foreground">{rw.reward_type}</td>
                  <td className="px-4 py-3 text-right">
                    {rw.reward_type === 'cash' ? `$${(rw.amount_cents / 100).toFixed(2)}` : '—'}
                  </td>
                  <td className="px-4 py-3"><ReferralStatusBadge status={rw.status} size="sm" /></td>
                  <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{formatDate(rw.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    {canPayout ? (
                      <div className="inline-flex gap-1">
                        {rw.status === 'pending' && (
                          <>
                            <ActionBtn label="Approve" onClick={() => transitionReward(rw.id, 'approve')} />
                            <ActionBtn label="Reject" variant="danger" onClick={() => transitionReward(rw.id, 'reject')} />
                          </>
                        )}
                        {rw.status === 'approved' && rw.reward_type === 'cash' && (
                          <ActionBtn label="Mark paid" onClick={() => transitionReward(rw.id, 'mark_paid')} />
                        )}
                        {rw.status === 'approved' && rw.reward_type === 'coupon' && (
                          <ActionBtn label="Mark granted" onClick={() => transitionReward(rw.id, 'mark_granted')} />
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {qrLink && (
        <QrModal url={`${linkBase}/r/${qrLink.code}`} code={qrLink.code} onClose={() => setQrLink(null)} />
      )}

      {mintOpen && (
        <MintLinkModal
          campaignId={id}
          apiUrl={apiUrl}
          onClose={() => setMintOpen(false)}
          onMinted={() => { setMintOpen(false); refetchLinks(); }}
        />
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b last:border-0 pb-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string | number }) {
  return (
    <div className="border rounded-lg p-4 bg-card">
      <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <div className="text-xl font-semibold capitalize">{value}</div>
    </div>
  );
}

function ActionBtn({ label, onClick, variant }: { label: string; onClick: () => void; variant?: 'danger' }) {
  return (
    <button
      onClick={onClick}
      className={`px-2 py-1 text-xs border rounded hover:bg-muted/30 ${variant === 'danger' ? 'text-destructive' : ''}`}
    >
      {label}
    </button>
  );
}

/**
 * Mint a referral link.
 *
 * 🔴 The UTM fields here are DROPDOWNS, never free text (fixed 2026-08-11).
 * This form writes `core.referral_links`, and `/r/{code}` composes those exact
 * values onto a live, publicly-redirecting URL — so a free-text `utm_source`
 * input meant an operator could publish `utm_source=IG` and fragment the
 * Instagram channel permanently, with no retroactive fix. Same guard, same
 * reason as the campaign taxonomy on the create page ("the anti-'IG vs
 * instagram' guard"); the vocabulary comes from `@/lib/utm-taxonomy` and the
 * backend re-validates it in createLinkRoute, because a UI is not a control.
 *
 * Tags stay OPTIONAL — a bare /r/ link with no campaign tagging is legitimate
 * and common. But once a source is picked, a medium is required: the channel in
 * every report is derived from the medium ALONE, and the INSERT defaults it to
 * `referral`, so "Instagram link, no medium" would silently store an Instagram
 * link that reports as channel `referral`.
 */
function MintLinkModal({
  campaignId,
  apiUrl,
  onClose,
  onMinted,
}: {
  campaignId: string;
  apiUrl: string;
  onClose: () => void;
  onMinted: () => void;
}) {
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [sourceKey, setSourceKey] = useState('');
  const [sourceSuffix, setSourceSuffix] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [iosUrl, setIosUrl] = useState('');
  const [androidUrl, setAndroidUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const normalized = code.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  const isFamily = SOURCE_PREFIXES.includes(sourceKey);
  const utmSource = isFamily ? `${sourceKey}${normalizeTag(sourceSuffix) ?? ''}` : sourceKey;
  const allowedMediums = mediumsForSource(sourceKey);

  // Untagged is a valid link; a PARTLY-tagged one is not. Once anything is set,
  // the same emission-strict check the backend runs decides whether this can be
  // submitted — minus the "missing" issues, which express the optionality above.
  const anyTag = !!(utmSource || utmMedium || utmCampaign);
  const blocking = anyTag
    ? validateUtmTuple({
        utm_source: utmSource,
        // Mirror the INSERT's COALESCE default so what is VALIDATED is what
        // will be STORED, not what was typed.
        utm_medium: utmMedium || 'referral',
        utm_campaign: utmCampaign,
      }).issues.filter((i) => !i.endsWith('_missing'))
    : [];
  const tagsOk = blocking.length === 0;

  const submit = async () => {
    if (!normalized || !tagsOk) return;
    setSubmitting(true);
    try {
      const idToken = await getIdToken();
      const r = await fetch(`${apiUrl}/v1/admin/referrals/links`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({
          code: normalized,
          campaign_id: campaignId,
          label: label.trim() || undefined,
          utm_source: utmSource || undefined,
          utm_medium: utmMedium || undefined,
          utm_campaign: utmCampaign || undefined,
          ios_store_url: iosUrl.trim() || undefined,
          android_store_url: androidUrl.trim() || undefined,
        }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({ message: 'Failed' }));
        throw new Error((e as { message?: string }).message ?? `HTTP ${r.status}`);
      }
      toast({ title: 'Link minted', description: `/r/${normalized}` });
      onMinted();
    } catch (err: unknown) {
      toast({ title: 'Mint failed', description: err instanceof Error ? err.message : 'error', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 border rounded-md text-sm bg-background';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      {/* 🔴 max-h + flex column is LOAD-BEARING, not styling (2026-09-18).
          Without it this card had no height constraint, and it GROWS: picking a
          UTM source reveals the medium and campaign fields. Measured on the live
          panel at a 1440x673 laptop viewport, the submit button landed at
          top:676 — three pixels below the fold, with the card unscrollable and
          the body scroll-locked behind the overlay. scrollIntoView() could not
          move it and no pointer click could reach it, so a real operator could
          SEE the button and never press it. Minting a link was impossible on a
          13-inch screen.
          The header and the action row stay put; only the fields scroll, so the
          primary action is always reachable however tall the form grows. This is
          the pattern RoleEditor.tsx already uses — it was simply never applied
          here. Pinned by tests/unit/lint/admin-modals-constrain-height.test.ts. */}
      <div className="bg-card border rounded-lg w-full max-w-md flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 pb-4 shrink-0">
          <h2 className="text-lg font-semibold">Mint referral link</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3 overflow-y-auto px-6 min-h-0">
          <div>
            <label className="block text-sm font-medium mb-1">Code</label>
            <input className={inputCls} value={code} placeholder="e.g. nathan-vip" onChange={(e) => setCode(e.target.value)} />
            {normalized && <p className="text-xs text-muted-foreground mt-1">/r/{normalized}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Label (optional)</label>
            <input className={inputCls} value={label} placeholder="Who / where this link is for" onChange={(e) => setLabel(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">UTM source (optional)</label>
            <select className={inputCls} value={sourceKey} onChange={(e) => { setSourceKey(e.target.value); setUtmMedium(''); }}>
              <option value="">No campaign tags</option>
              {UTM_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              {SOURCE_PREFIXES.map((p) => <option key={p} value={p}>{p}… (name it below)</option>)}
            </select>
            {isFamily && (
              <input
                className={`${inputCls} mt-2`}
                value={sourceSuffix}
                placeholder={sourceKey === 'influencer_' ? 'janedoe' : 'acme_media'}
                onChange={(e) => setSourceSuffix(e.target.value)}
                onBlur={(e) => setSourceSuffix(normalizeTag(e.target.value) ?? '')}
                aria-label="Source name"
              />
            )}
            {utmSource && <p className="text-xs text-muted-foreground mt-1">utm_source=<code>{utmSource}</code></p>}
          </div>
          {sourceKey && (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">UTM medium</label>
                <select className={inputCls} value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)}>
                  <option value="">Select a medium…</option>
                  {allowedMediums.map((m) => (
                    <option key={m} value={m}>{m} → channel {MEDIUM_TO_CHANNEL[m]}</option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Required once a source is set — the channel in every report is derived from this alone.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">UTM campaign (optional)</label>
                <input
                  className={inputCls}
                  value={utmCampaign}
                  placeholder="202608_launch"
                  onChange={(e) => setUtmCampaign(e.target.value)}
                  onBlur={(e) => setUtmCampaign(normalizeTag(e.target.value) ?? '')}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  <code>YYYYMM_shortname</code>. Reuse the existing slug for this initiative — build one
                  in <strong>Campaign Links</strong> if you need to see what already exists.
                </p>
              </div>
            </>
          )}
          {!tagsOk && (
            <p className="text-xs text-destructive">
              {blocking.includes('medium_not_valid_for_source')
                ? `"${utmMedium || 'referral'}" is not a medium we use with "${utmSource}".`
                : blocking.includes('campaign_malformed')
                  ? 'Campaign must be YYYYMM_shortname (e.g. 202608_launch).'
                  : 'These tags are not in the canonical taxonomy.'}
            </p>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">iOS App Store URL (optional)</label>
            <input className={inputCls} value={iosUrl} placeholder="https://apps.apple.com/app/apple-store/id…?pt=…&mt=8" onChange={(e) => setIosUrl(e.target.value)} />
            <p className="text-xs text-muted-foreground mt-1">
              Include Apple&apos;s <code>pt=</code> provider token and we auto-append <code>ct={'{code}'}</code> — App Analytics then counts installs per link.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Android Play URL (optional)</label>
            <input className={inputCls} value={androidUrl} placeholder="https://play.google.com/store/apps/details?id=…" onChange={(e) => setAndroidUrl(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 p-6 pt-5 shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded-md hover:bg-muted/30">Cancel</button>
          <button
            disabled={!normalized || !tagsOk || submitting}
            onClick={submit}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
          >
            <Check className="w-4 h-4" />
            {submitting ? 'Minting…' : 'Mint link'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** QR export for a referral link — for flyers, events, print (Paula's spec #6). */
function QrModal({ url, code, onClose }: { url: string; code: string; onClose: () => void }) {
  const canvasWrapRef = useRef<HTMLDivElement>(null);

  const download = () => {
    const canvas = canvasWrapRef.current?.querySelector('canvas');
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `abby-qr-${code}.png`;
    a.click();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card border rounded-lg p-6 w-full max-w-sm text-center" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">QR — {code}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>
        <div ref={canvasWrapRef} className="inline-block bg-white p-4 rounded-lg">
          <QRCodeCanvas value={url} size={512} style={{ width: 224, height: 224 }} level="M" includeMargin />
        </div>
        <p className="text-xs text-muted-foreground mt-3 break-all">{url}</p>
        <button
          onClick={download}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90"
        >
          Download PNG (print-ready)
        </button>
      </div>
    </div>
  );
}
